// PaymentsPage.jsx (compatible with your backend)
// - Loads /api/projects
// - For each project, loads /api/payments/project/:projectId (or /api/payments?project_id=)
// - Loads /api/milestones/project/:projectId (or /api/milestones?project_id=)
// - Merges results to display a single table of payments (view-only)

import React, { useEffect, useState, useRef } from "react";
import "./PaymentsPage.css";

const API_BASE = process.env.REACT_APP_API_BASE || `${process.env.REACT_APP_API_URL || ""}`;

function apiHeaders(json = true) {
  const headers = {};
  if (json) headers["Content-Type"] = "application/json";
  const token = localStorage.getItem("token");
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

function fmtCurrency(val, currency = "USD") {
  if (val == null || val === "") return "—";
  const n = Number(val) || 0;
  try {
    return n.toLocaleString(undefined, { style: "currency", currency, maximumFractionDigits: 2 });
  } catch {
    return n.toFixed(2);
  }
}

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]); // combined payments across projects
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const timeoutRef = useRef(null);
  const mountedRef = useRef(true);

  function showMsg(text) {
    setError(text);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (mountedRef.current) setError("");
    }, 6000);
  }

  // attempt multiple endpoints for payments/milestones to be resilient to API differences
  async function fetchJsonSafe(url, treat404AsEmpty = false) {
    try {
      const res = await fetch(url, { headers: apiHeaders(false) });
      if (!res.ok) {
        if (res.status === 404 && treat404AsEmpty) return [];
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const ct = res.headers.get("content-type") || "";
      if (ct.includes("application/json")) return await res.json();
      // if not json, return empty array
      return [];
    } catch (err) {
      // bubble up error for caller to handle/log
      throw err;
    }
  }

  async function loadAll() {
    setLoading(true);
    setError("");
    setPayments([]);
    setProjects([]);

    try {
      // 1) Load projects
      const projRes = await fetch(`${API_BASE}/api/projects`, { headers: apiHeaders(false) });
      if (!projRes.ok) {
        const txt = await projRes.text().catch(() => "");
        throw new Error(txt || `Failed to load projects (${projRes.status})`);
      }
      const projData = await projRes.json();
      if (!mountedRef.current) return;
      const projectsArr = Array.isArray(projData) ? projData : [];
      setProjects(projectsArr);

      // 2) For each project, fetch payments and milestones in parallel.
      // We'll try multiple URL shapes to be tolerant.
      const perProjectPromises = projectsArr.map(async (p) => {
        const pid = Number(p.id);
        const paymentsUrls = [
          `${API_BASE}/api/payments/project/${pid}`,
          `${API_BASE}/api/payments?project_id=${pid}`,
          `${API_BASE}/api/payments?project=${pid}`,
        ];
        const milestonesUrls = [
          `${API_BASE}/api/milestones/project/${pid}`,
          `${API_BASE}/api/milestones?project_id=${pid}`,
        ];

        // try payments urls in order until one succeeds
        let paymentsForProject = [];
        for (const u of paymentsUrls) {
          try {
            paymentsForProject = await fetchJsonSafe(u, true);
            if (Array.isArray(paymentsForProject)) break;
          } catch (e) {
            console.warn("payments fetch try failed:", u, e.message);
            paymentsForProject = [];
          }
        }

        // try milestones urls
        let milestonesForProject = [];
        for (const u of milestonesUrls) {
          try {
            milestonesForProject = await fetchJsonSafe(u, true);
            if (Array.isArray(milestonesForProject)) break;
          } catch (e) {
            console.warn("milestones fetch try failed:", u, e.message);
            milestonesForProject = [];
          }
        }

        return {
          project: p,
          payments: Array.isArray(paymentsForProject) ? paymentsForProject : [],
          milestones: Array.isArray(milestonesForProject) ? milestonesForProject : [],
        };
      });

      const perProjectResults = await Promise.all(perProjectPromises);
      if (!mountedRef.current) return;

      // flatten and annotate
      const combined = [];
      for (const item of perProjectResults) {
        const proj = item.project || {};
        const projName = proj.name || `#${proj.id}`;
        const milestoneMap = new Map();
        (item.milestones || []).forEach((m) => milestoneMap.set(Number(m.id), m));

        (item.payments || []).forEach((pay) => {
          const milestone = pay.milestone_id ? milestoneMap.get(Number(pay.milestone_id)) : null;
          combined.push({
            ...pay,
            project_name: projName,
            milestone_title: milestone ? milestone.title : (pay.milestone_title || null),
          });
        });
      }

      // sort newest first (try payment_date then id desc)
      combined.sort((a, b) => {
        const da = a.payment_date ? new Date(a.payment_date).getTime() : 0;
        const db = b.payment_date ? new Date(b.payment_date).getTime() : 0;
        if (db !== da) return db - da;
        return Number(b.id || 0) - Number(a.id || 0);
      });

      if (mountedRef.current) {
        setPayments(combined);
      }
    } catch (err) {
      console.error("loadAll error:", err);
      if (mountedRef.current) showMsg(err.message || "Failed to load payments");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }

  useEffect(() => {
    mountedRef.current = true;
    loadAll();
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // filtering
  const q = query.trim().toLowerCase();
  const filtered = payments.filter((p) => {
    if (!q) return true;
    return (
      (p.project_name || "").toString().toLowerCase().includes(q) ||
      (p.milestone_title || "").toString().toLowerCase().includes(q) ||
      (p.method || "").toString().toLowerCase().includes(q) ||
      (p.category || "").toString().toLowerCase().includes(q) ||
      (p.status || "").toString().toLowerCase().includes(q) ||
      String(p.id || "").includes(q)
    );
  });

  return (
    <div className="payments-view-page">
      <header className="payments-header">
        <div>
          <h2>Payments</h2>
          <p className="sub">All payments (advance, milestone payments — view only)</p>
        </div>

        <div className="payments-controls">
          <input
            className="search"
            placeholder="Search by project, milestone, method, status or id…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button className="btn" onClick={loadAll} disabled={loading}>
            Refresh
          </button>
        </div>
      </header>

      {error && <div className="payments-error">Error: {error}</div>}

      <section className="payments-list">
        <div className="table-wrapper">
          <table className="payments-table" role="table" aria-label="Payments">
            <thead>
              <tr>
                <th>ID</th>
                <th>Project</th>
                <th>Milestone</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Payment Date</th>
                <th>Method</th>
                <th>Category</th>
                <th>Status</th>
                <th>Notes</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="empty">Loading…</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan="10" className="empty">No payments found</td>
                </tr>
              ) : (
                filtered.map((p) => (
                  <tr key={`${p.id}-${p.project_id || "x"}`}>
                    <td>{p.id}</td>
                    <td>{p.project_name || (p.project_id ? `#${p.project_id}` : "—")}</td>
                    <td>{p.milestone_title || (p.milestone_id ? `#${p.milestone_id}` : "—")}</td>
                    <td>{p.type || "-"}</td>
                    <td>{p.amount != null ? fmtCurrency(p.amount, p.currency || "USD") : "-"}</td>
                    <td>{p.payment_date ? new Date(p.payment_date).toLocaleDateString() : "-"}</td>
                    <td>{p.method || "-"}</td>
                    <td>{p.category || "-"}</td>
                    <td>{p.status || "-"}</td>
                    <td className="notes-cell" title={p.notes || ""}>{p.notes || "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
